import { useState } from "react";
import type { DeleteAccountPayload } from "api";
import { Modal } from "components/ui/Modal";
import { clearStoredUser } from "utils/user.utils";

const CONFIRMATION_WORD = "DELETE";

type DeleteAccountCardProps = {
  /** Provider accounts have no password to re-enter. */
  needsPassword: boolean;
  onDelete: (payload: DeleteAccountPayload) => Promise<string | null>;
};

export const DeleteAccountCard = ({ needsPassword, onDelete }: DeleteAccountCardProps) => {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState("");
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = word.trim().toUpperCase() === CONFIRMATION_WORD && (!needsPassword || password !== "");

  const close = () => {
    setOpen(false);
    setWord("");
    setPassword("");
    setError(null);
  };

  const confirm = async () => {
    setDeleting(true);
    setError(null);

    const message = await onDelete({
      confirmation: CONFIRMATION_WORD,
      ...(needsPassword ? { password } : {}),
    });

    setDeleting(false);

    if (message !== null) {
      setError(message);
      return;
    }

    // The session is gone server-side; drop the copy the site header reads so
    // it does not keep showing a signed-in account on the way out.
    clearStoredUser();
    window.location.assign("/");
  };

  return (
    <div className="pcx pcx--danger">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">Delete account</div>
          <p className="pcx__sub">Removes the workspace, all API keys and usage history. This cannot be undone.</p>
        </div>
        <button type="button" className="pcbtn pcbtn--bad" onClick={() => setOpen(true)}>
          Delete account
        </button>
      </div>

      {open && (
        <Modal
          title="Delete this account?"
          onClose={close}
          actions={
            <>
              <button
                type="button"
                className="pcbtn pcbtn--danger"
                disabled={!ready || deleting}
                onClick={() => void confirm()}
              >
                {deleting ? "Deleting…" : "Delete account"}
              </button>
              <button type="button" className="pcbtn" onClick={close}>
                Cancel
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            The workspace, every API key and all usage history will be deleted permanently. Running integrations will
            stop working immediately. This cannot be undone.
          </p>

          <label className="pcmodal__f">
            <span>
              Type <b>{CONFIRMATION_WORD}</b> to confirm
            </span>
            <input
              type="text"
              value={word}
              placeholder={CONFIRMATION_WORD}
              autoComplete="off"
              onChange={(event) => setWord(event.target.value)}
            />
          </label>

          {needsPassword && (
            <label className="pcmodal__f">
              <span>Your password</span>
              <input
                type="password"
                value={password}
                placeholder="••••••••••"
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
          )}

          {error && <p className="pc-note pc-note--bad">{error}</p>}
        </Modal>
      )}
    </div>
  );
};
